import { ICrop, IHolding } from "@omnia/types"
import { IDashboardNutrition, IDashboardService } from "../types";
import { useEffect, useMemo } from "react";
import { getMainCropFromCropping } from "../nmax";

import m = omnia.models;

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    svc: IDashboardService;
    nvz: IDashboardNutrition | undefined;
    holding: IHolding | undefined;
    /** Callback executed when error status of the component changes */
    onErrorChange?: (isError: boolean) => void;
}

/** Component showing the "N Max" area. Sets class to 'nmax' (you can add more classes by className). */
export function AreaNMax({ svc, nvz, holding, onErrorChange, className, ...props }: Props) {

    const scotland = holding?.Region == m.RegionId.Scotland;
    const showCropNames = scotland;

    const nmaxGroups = useMemo(() => {
        if (!(nvz?.NMax?.length > 0)) {
            return [];
        }

        const unsorted = nvz.NMax.map(g => {
    
                var ngroup = new NMaxGroup({
                    name: g.GroupName,
                    groupId: g.GroupId,
                    NMax: g.NMaxAverage || 0,
                    NRequired: g.NRequiredAverage,
                    NApplied: g.NAppliedAverage,
                    mainCrop: getMainCropFromCropping(g.Cropping, svc)
                });
    
                if (ngroup.mainCrop && showCropNames) {
                    ngroup.name = (ngroup.mainCrop.Grass ? "Grass" : ngroup.mainCrop.Name)
                }
    
                return ngroup;
            });

        return unsorted.sort((g1, g2) => {
            let d = (g1.breaksNMax ? 0 : 1) - (g2.breaksNMax ? 0 : 1);
            if (!d) d = g1.groupId - g2.groupId;
            return d;
        });

    }, [nvz?.NMax, svc, svc.yearId, svc.stateVersion, showCropNames]);

    const breaksNMax = nmaxGroups?.some(g => g.breaksNMax) ?? false;

    useEffect(() => {
        if (onErrorChange) {
            const isError = breaksNMax;
            onErrorChange(isError);
        }
    }, [breaksNMax, onErrorChange]);

    return (       
        <div className={`${className || ''} nmax`} {...props}>

            <h4 className={ breaksNMax ? 'nok' : '' }>
                N Max
            </h4>

            {(breaksNMax && scotland) &&
                <p className="nok">
                    <i className="icon icon-error"></i>
                    This holding has fields that do not comply with the N max limit. Please see the table below.
                </p>
            }

            {(breaksNMax && (!scotland)) &&
                <p className="nok">
                    <i className="icon icon-error"></i>
                    At least one crop group does not comply with the N max limit. Please see the table below.
                </p>
            }

            {(!breaksNMax) &&
                <p>
                    <i className="icon icon-ok"></i>
                    This holding complies with the N max limit.
                </p>
            }

            <table className="omnia-table">
                <thead>
                    <tr>
                        <th>Group</th>
                        <th className="n">Adjusted N max</th>
                        <th className="n">Required N</th>
                        <th className="n">Applied N</th>
                    </tr>
                </thead>
                <tbody>
                    {nmaxGroups?.map((g, index) => {
                        return (
                            <tr key={index} className={g.breaksNMax ? 'nok' : ''}>
                                <td>
                                    {!g.breaksNMax &&
                                        <span className="omnia-crop-circle" style={{ backgroundColor: g.getColor }} />
                                    }
                                    {g.breaksNMax &&
                                        <i className="icon icon-error"></i>
                                    }
                                    {g.name}
                                </td>
                                <td className="n">
                                    {g.NMax}
                                </td>
                                <td className="n">
                                    {g.NRequired}
                                </td>
                                <td className="n">
                                    {g.NApplied}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    );
}

interface NMaxGroupProps {
    name: string;
    groupId: number;
    mainCrop: ICrop;
    NMax: number;
    NRequired: number;
    NApplied: number;
}

class NMaxGroup {

    public name: string;
    public groupId: number;
    public mainCrop: ICrop;
    public NMax: number;
    public NRequired: number;
    public NApplied: number;

    constructor(props: NMaxGroupProps){
        Object.assign(this, props);
    }

    get getColor(): any {
        return omnia.fields.getCropCssColor(this.mainCrop);
    }

    get breaksNMax(): boolean {
        if (this.NRequired > this.NMax) return true;
        if (this.NApplied > this.NMax) return true;
        return false;
    }
}